import React from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import strings from 'strings';
import { additionalPeriodRanges } from 'UI/components/templates/SideFiltersLayout/filters';
import { createCustomStaticRanges } from 'UI/utils';

import { DateRange } from '../InlineFilters/components/DateRange';
import { StatsItemContainer } from '../StatsItemContainer';

import { CHART_COLORS } from './GaugeStats.constants';
import { useStyles } from './GaugeStats.styles';
import { buildGaugeArcs, buildNeedle, needleAngleDeg } from './GaugeStats.utils';

const GAUGE = {
  cx: 108,
  cy: 108,
  outerRadius: 100,
  innerRadius: 80,
  needleScale: 1,
  viewBox: '0 0 216 120'
};

// Arcs and the baseline needle are prop-independent — compute them once.
const GAUGE_ARCS = buildGaugeArcs({
  arcsLength: [0.5, 0.3, 0.2],
  colors: [CHART_COLORS.red, CHART_COLORS.yellow, CHART_COLORS.green],
  cx: GAUGE.cx,
  cy: GAUGE.cy,
  outerRadius: GAUGE.outerRadius,
  innerRadius: GAUGE.innerRadius
});

const NEEDLE = buildNeedle({
  cx: GAUGE.cx,
  cy: GAUGE.cy,
  outerRadius: GAUGE.outerRadius,
  needleScale: GAUGE.needleScale
});

const STRINGS = strings.commandCenter.activity.gaugeStats;

export const GaugeStats = ({ id, stats, dateRange, onDateRangeChange, dateRangerPickerProps }) => {
  const classes = useStyles();
  const percent = stats[0]?.percentage ?? 0;
  const needleRotation = needleAngleDeg(percent > 1 ? 1 : percent);

  return (
    <>
      <DateRange
        DateRangerPickerProps={{
          staticRanges: createCustomStaticRanges(additionalPeriodRanges),
          ...dateRangerPickerProps
        }}
        defaultRange={dateRange}
        disableTimeRange
        monthsToShow={1}
        onPeriodChange={onDateRangeChange}
      />

      <Grid container className={classes.flexAlignCenterSpaceBetween}>
        <Grid item lg={6} sm={12} className={classes.gaugeChartContainer}>
          <Typography className={classes.textCenter}>{STRINGS.scaleLabel}</Typography>
          <svg
            id={id}
            data-testid={`gauge-${id}`}
            className={classes.gauge}
            viewBox={GAUGE.viewBox}
          >
            {GAUGE_ARCS.map(arc => (
              <path key={arc.color} d={arc.path} fill={arc.color} />
            ))}
            <g
              className={classes.needle}
              style={{
                transform: `rotate(${needleRotation}deg)`,
                transformOrigin: `${NEEDLE.origin.x}px ${NEEDLE.origin.y}px`
              }}
            >
              <path d={NEEDLE.path} fill={CHART_COLORS.needle} />
              <circle
                cx={NEEDLE.base.cx}
                cy={NEEDLE.base.cy}
                r={NEEDLE.base.r}
                fill={CHART_COLORS.needle}
              />
            </g>
          </svg>

          <Typography variant="h6" className={classes.textCenter}>
            {strings.formatString(STRINGS.percentage, { value: Math.round(percent * 100) })}
          </Typography>
        </Grid>

        <Grid item lg={4} sm={12}>
          <StatsItemContainer items={stats} />
        </Grid>
      </Grid>
    </>
  );
};
